import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CreditCard, KeyRound, Crown } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { getProEmail, setProEmail } from "@/lib/account";

const inputCls = "flex-1 rounded-full bg-[#090A0F] border border-white/10 px-5 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-500/60 transition-colors";

export const CheckoutForm = ({ plan, planName, price, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail || getProEmail() || "");
  const [loading, setLoading] = useState(false);
  const [paypal, setPaypal] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/payments/paypal/config`).then((r) => setPaypal(r.data.enabled)).catch(() => {});
  }, []);

  const payPaypal = async () => {
    if (!formRef.current.reportValidity()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/paypal/subscribe`, { plan, email, origin_url: window.location.origin });
      setProEmail(email);
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile avviare PayPal. Riprova.");
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { plan, email, origin_url: window.location.origin });
      setProEmail(email);
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Impossibile avviare il pagamento. Controlla l'email e riprova.");
      setLoading(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={submit} data-testid="checkout-form" className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] to-[#0d0f17] p-8 sm:p-10 grid lg:grid-cols-2 gap-8 items-center">
      <div>
        <div className="flex items-center gap-2 font-display text-xl font-bold"><CreditCard className="w-5 h-5 text-amber-400" /> Abbonati a {planName}</div>
        <p className="text-slate-400 mt-2 text-sm">{price}/mese, disdici quando vuoi. Il Pro si attiva sull'email che inserisci.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="la-tua@email.it" data-testid="input-checkout-email" className={inputCls} />
        <button type="submit" disabled={loading} data-testid="btn-checkout"
          className="rounded-full bg-amber-500 px-6 py-3 font-semibold text-sm text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-50">
          {loading ? "Reindirizzamento…" : paypal ? "Paga con carta" : "Vai al pagamento"}
        </button>
        {paypal && (
          <button type="button" onClick={payPaypal} disabled={loading} data-testid="btn-checkout-paypal"
            className="rounded-full bg-[#FFC439] px-6 py-3 font-semibold text-sm text-[#003087] transition-colors hover:bg-[#f2b92f] disabled:opacity-50">
            PayPal
          </button>
        )}
      </div>
    </form>
  );
};

export const RecoverPro = () => {
  const [email, setEmail] = useState("");
  const [pro, setPro] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const { data } = await axios.get(`${API}/pro/status`, { params: { email } });
    if (data.pro) {
      setProEmail(email);
      setPro(data.plan);
      toast.success("Pro attivato su questo dispositivo");
    } else {
      toast.error("Nessun abbonamento attivo per questa email");
    }
  };

  if (pro) return <p className="text-emerald-300 text-sm flex items-center gap-2" data-testid="recover-success"><Crown className="w-4 h-4" /> Piano {pro} attivo su questo dispositivo.</p>;

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-testid="recover-form">
      <span className="flex items-center gap-2 text-sm text-slate-400 shrink-0"><KeyRound className="w-4 h-4" /> Hai già il Pro?</span>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email usata per l'abbonamento" data-testid="input-recover-email" className={inputCls} />
      <button type="submit" data-testid="btn-recover" className="rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-slate-200 hover:border-amber-500/60 hover:text-amber-300 transition-colors">Recupera</button>
    </form>
  );
};
